<template>
    <div class="data-center-container">
        <!-- 数据中心卡片 -->
        <el-card class="data-card" shadow="never">
            <el-page-header content="积分管理" icon="database" title="积分管理列表" style="margin-bottom: 20px;" />

            <!-- 工具栏：搜索 + 新增积分 + 批量操作 -->
            <div class="toolbar">
                <el-input v-model="searchText" placeholder="搜索UID或用户名" :prefix-icon="Search" clearable
                    style="width: 300px; margin-right: 16px;" />
                <el-button type="primary" :icon="Plus" @click="goToAdd">
                    新增积分
                </el-button>
                <!-- 新增：批量操作按钮组 -->
                <el-button-group style="margin-left: 16px;">
                    <el-button type="warning" :icon="Refresh" :disabled="selectedRows.length === 0 || batchLoading"
                        @click="batchReset">
                        批量重置
                    </el-button>
                    <el-button type="danger" :icon="Delete" :disabled="selectedRows.length === 0 || batchLoading"
                        @click="batchDelete">
                        批量删除
                    </el-button>
                </el-button-group>
            </div>

            <!-- 用户列表表格：增加多选列 -->
            <el-table :data="filteredTableData" style="width: 100%;" row-key="id" v-loading="tableLoading" stripe
                @selection-change="handleSelectionChange">
                <!-- 新增：多选框列 -->
                <el-table-column type="selection" width="55" />
                <el-table-column prop="uid" label="B站UID" />
                <el-table-column prop="integral" label="积分余额" />
                <el-table-column prop="name" label="用户名" />
                <el-table-column label="记录">
                    <template #default="scope">
                        <el-tooltip :content="scope.row.Recording" placement="top">
                            <div class="content-ellipsis">{{ scope.row.Recording }}</div>
                        </el-tooltip>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="280">
                    <template #default="scope">
                        <el-button :icon="Search" circle type="primary" size="small" @click="handleLook(scope.row)"
                            title="查看详情" />
                        <!-- 新增：编辑按钮 -->
                        <el-button :icon="EditPen" circle type="success" size="small" @click="handleEdit(scope.row)"
                            title="编辑" />
                        <el-button :icon="Refresh" circle type="warning" size="small" @click="handleReset(scope.row)"
                            title="重置积分" />
                        <el-button :icon="Delete" circle type="danger" size="small" @click="handleDelete(scope.row)"
                            title="删除记录" />
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- 详情弹窗（不变） -->
        <el-dialog v-model="dialogVisible" title="积分详情" width="60%">
            <div>
                <h3>B站UID：{{ previewData.uid }}</h3><br>
                <h4>积分余额：{{ previewData.integral }}</h4><br>
                <h4>用户名：{{ previewData.name }}</h4><br>
                <h4>记录：</h4>
                <p v-html="formattedRecording"></p>
                <el-divider>
                    <el-icon>
                        <StarFilled />
                    </el-icon>
                </el-divider>
            </div>
        </el-dialog>

        <!-- 新增：编辑弹窗 -->
        <el-dialog v-model="editDialogVisible" title="编辑积分信息" width="500px">
            <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="100px">
                <el-form-item label="B站UID">
                    <el-input v-model="editForm.uid" disabled />
                </el-form-item>
                <el-form-item label="用户名">
                    <el-input v-model="editForm.name" />
                </el-form-item>
                <el-form-item label="积分余额" prop="integral">
                    <el-input-number v-model="editForm.integral" :min="0" :precision="0" style="width: 100%;" />
                </el-form-item>
                <el-form-item label="记录" prop="Recording">
                    <el-input v-model="editForm.Recording" type="textarea" :rows="4" placeholder="请输入记录内容" />
                    <!-- 新增提示 -->
                    <div class="form-tip">提示：多个记录请用 / 分隔</div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="editDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitEdit" :loading="editLoading">保存</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import {
    Delete,
    EditPen,
    Search,
    StarFilled,
    Refresh,
    Plus
} from '@element-plus/icons-vue'
import { ref, onMounted, computed, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { escapeHtml } from '../../../util/escapeHtml'

const router = useRouter()

// 数据状态
const tableData = ref([])
const tableLoading = ref(false)
const dialogVisible = ref(false)
const searchText = ref('')
const selectedRows = ref([])          // 新增：选中的行
const batchLoading = ref(false)       // 新增：批量操作加载状态

// 编辑弹窗相关
const editDialogVisible = ref(false)
const editLoading = ref(false)
const editFormRef = ref(null)
const editForm = ref({
    uid: '',
    name: '',
    integral: 0,
    Recording: ''
})

// 编辑表单校验规则
const editRules = {
    integral: [
        { required: true, message: '请输入积分余额', trigger: 'blur' },
        { type: 'number', min: 0, message: '积分余额不能为负数', trigger: 'blur' }
    ],
    Recording: [
        { required: false, message: '请输入记录', trigger: 'blur' }
    ]
}

// 计算属性 - 过滤表格数据
const filteredTableData = computed(() => {
    if (!searchText.value) return tableData.value
    const keyword = searchText.value.toLowerCase().trim()
    return tableData.value.filter(item =>
        String(item.uid).toLowerCase().includes(keyword) ||
        String(item.name).toLowerCase().includes(keyword)
    )
})

// 数据获取
const fetchData = async () => {
    tableLoading.value = true
    try {
        const res = await axios.get('/adminapi/integral/list')
        if (res.data) {
            tableData.value = res.data.results || []
        } else {
            ElMessage.error('数据加载失败')
        }
    } catch (error) {
        console.error('请求错误:', error)
        ElMessage.error('数据加载失败')
    } finally {
        tableLoading.value = false
    }
}

onMounted(() => {
    fetchData()
})

// 表格多选变化
const handleSelectionChange = (selection) => {
    selectedRows.value = selection
}

// 查看详情
const previewData = ref({})
const handleLook = (data) => {
    dialogVisible.value = true
    previewData.value = data
}

// 重置积分（单条）
const handleReset = async (row) => {
    try {
        await axios.post('/adminapi/integral/rest', { uid: row.uid })
        ElMessage.success('积分重置成功')
        fetchData()
    } catch (error) {
        console.error('积分重置错误:', error)
        ElMessage.error('积分重置失败，请稍后重试')
    }
}

// 删除记录（单条）
const handleDelete = async (row) => {
    try {
        await ElMessageBox.confirm(
            '确定要删除该用户的积分记录吗？此操作不可恢复！',
            '删除确认',
            {
                confirmButtonText: '确定删除',
                cancelButtonText: '取消',
                type: 'warning'
            }
        )
        await axios.post('/adminapi/integral/del', { uid: row.uid })
        ElMessage.success('记录删除成功')
        fetchData()
    } catch (error) {
        if (error !== 'cancel') {
            console.error('记录删除错误:', error)
            ElMessage.error('记录删除失败，请稍后重试')
        } else {
            ElMessage.info('已取消删除')
        }
    }
}

// 新增：编辑按钮 - 打开编辑弹窗
const handleEdit = (row) => {
    editForm.value = {
        uid: row.uid,
        name: row.name,
        integral: Number(row.integral) || 0,
        Recording: row.Recording || ''
    }
    editDialogVisible.value = true
    // 清除表单校验状态
    nextTick(() => {
        editFormRef.value?.clearValidate()
    })
}

// 新增：提交编辑
const submitEdit = async () => {
    try {
        await editFormRef.value.validate()
        editLoading.value = true
        // 调用修改接口
        await axios.post('/adminapi/integral/change', {
            uid: editForm.value.uid,
            integral: editForm.value.integral,
            Recording: editForm.value.Recording
        })
        ElMessage.success('修改成功')
        editDialogVisible.value = false
        fetchData()
    } catch (error) {
        console.error('编辑失败:', error)
        ElMessage.error(error.response?.data?.message || '修改失败，请重试')
    } finally {
        editLoading.value = false
    }
}

// 新增：批量重置
const batchReset = async () => {
    if (selectedRows.value.length === 0) return
    try {
        await ElMessageBox.confirm(
            `确定要重置选中的 ${selectedRows.value.length} 个用户的积分吗？`,
            '批量重置确认',
            { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        )
        batchLoading.value = true
        const promises = selectedRows.value.map(row =>
            axios.post('/adminapi/integral/rest', { uid: row.uid })
        )
        const results = await Promise.allSettled(promises)
        const successCount = results.filter(r => r.status === 'fulfilled').length
        const failCount = results.filter(r => r.status === 'rejected').length
        if (failCount === 0) {
            ElMessage.success(`批量重置成功，共处理 ${successCount} 条记录`)
        } else {
            ElMessage.warning(`批量重置完成，成功 ${successCount} 条，失败 ${failCount} 条，请查看控制台日志`)
            results.forEach((r, index) => {
                if (r.status === 'rejected') {
                    console.error(`用户 ${selectedRows.value[index].uid} 重置失败:`, r.reason)
                }
            })
        }
        fetchData()
    } catch (error) {
        if (error !== 'cancel') {
            console.error('批量重置失败:', error)
            ElMessage.error('批量重置失败，请重试')
        }
    } finally {
        batchLoading.value = false
    }
}

// 新增：批量删除
const batchDelete = async () => {
    if (selectedRows.value.length === 0) return
    try {
        await ElMessageBox.confirm(
            `确定要永久删除选中的 ${selectedRows.value.length} 个用户的积分记录吗？此操作不可恢复！`,
            '批量删除确认',
            { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
        )
        batchLoading.value = true
        const promises = selectedRows.value.map(row =>
            axios.post('/adminapi/integral/del', { uid: row.uid })
        )
        const results = await Promise.allSettled(promises)
        const successCount = results.filter(r => r.status === 'fulfilled').length
        const failCount = results.filter(r => r.status === 'rejected').length
        if (failCount === 0) {
            ElMessage.success(`批量删除成功，共删除 ${successCount} 条记录`)
        } else {
            ElMessage.warning(`批量删除完成，成功 ${successCount} 条，失败 ${failCount} 条，请查看控制台日志`)
            results.forEach((r, index) => {
                if (r.status === 'rejected') {
                    console.error(`用户 ${selectedRows.value[index].uid} 删除失败:`, r.reason)
                }
            })
        }
        fetchData()
    } catch (error) {
        if (error !== 'cancel') {
            console.error('批量删除失败:', error)
            ElMessage.error('批量删除失败，请重试')
        }
    } finally {
        batchLoading.value = false
    }
}

// 跳转到积分添加页面
const goToAdd = () => {
    router.push('/admin/integral/add')
}

// 记录格式化（Recording 为后端任意字符串，先 HTML 转义再拼 <br/> 防 XSS）
const formattedRecording = computed(() => {
    return previewData.value.Recording ? escapeHtml(previewData.value.Recording).replaceAll('/', '<br/>') : ''
})
</script>

<style lang="scss" scoped>
.data-center-container {
    padding: 20px 30px;
    min-height: calc(100vh - 80px);

    .data-card {
        .el-page-header {
            margin-bottom: 20px;
            padding: 15px 0;
            border-bottom: 1px solid #ebeef5;
        }

        .toolbar {
            display: flex;
            align-items: center;
            margin-bottom: 20px;
            flex-wrap: wrap;
            gap: 10px;
        }

        .el-table {
            margin-bottom: 20px;

            thead {
                background-color: #f5f7fa;
            }

            .el-table-column__header {
                color: #303133;
                font-weight: 500;
                padding: 12px 0;
                text-align: center;
            }

            .el-table-cell {
                padding: 12px 0;
                color: #606266;
                text-align: center;

                :deep(.el-button) {
                    margin: 0 4px;
                }
            }
        }
    }
}

.content-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 250px;
}

// 新增编辑弹窗样式（可选）
:deep(.el-dialog) {
    border-radius: 8px;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    line-height: 1.2;
}
</style>